import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style';
import { DOMParser as PMDOMParser, Slice } from '@tiptap/pm/model';
import { ParagraphStyle } from './editor/ParagraphStyle';
import { TextSizeSelect, ColorPicker } from './editor/ToolbarFormatControls';
import { toEditorHtml, plainTextToParagraphs } from '../lib/storyHtml';
import { cleanPastedHtml } from '../lib/wordPaste';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo, Redo, AlignLeft, AlignCenter, AlignRight,
  Minus, HelpCircle
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`shrink-0 p-1.5 rounded transition-colors ${
      isActive
        ? 'bg-brand-primary text-white'
        : 'text-text-muted hover:bg-brand-primary/10 hover:text-text-primary'
    } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div
      className="sticky z-20 flex flex-nowrap sm:flex-wrap items-center gap-0.5 px-4 py-2 border-b border-[#E5E2DC] bg-[#FAF8F5] overflow-x-auto sm:overflow-visible"
      style={{ top: 'var(--editor-header-h, 0px)' }}
      data-testid="editor-toolbar"
    >
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </MenuButton>
      <TextSizeSelect editor={editor} />
      <ColorPicker editor={editor} />

      <div className="w-px h-5 bg-[#E5E2DC] mx-1 shrink-0" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-[#E5E2DC] mx-1 shrink-0" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Divider"
      >
        <Minus className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-[#E5E2DC] mx-1 shrink-0" />

      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-[#E5E2DC] mx-1 shrink-0" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-[#E5E2DC] mx-1 shrink-0" />

      <div className="relative group hidden sm:block" data-testid="poetry-help-btn">
        <button
          type="button"
          className="p-1.5 rounded text-text-muted hover:bg-brand-primary/10 hover:text-text-primary cursor-pointer transition-colors"
          title="Formatting tips"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
        <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-[#E5E2DC] rounded-lg shadow-lg p-3 text-xs text-text-secondary opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
          <p className="font-semibold text-text-primary mb-1.5">Formatting Tips</p>
          <p className="mb-1"><kbd className="px-1 py-0.5 bg-surface-alt rounded text-[10px]">Enter</kbd> New line</p>
          <p className="mb-1"><kbd className="px-1 py-0.5 bg-surface-alt rounded text-[10px]">Enter</kbd> twice &rarr; Stanza break</p>
          <p><kbd className="px-1 py-0.5 bg-surface-alt rounded text-[10px]">Shift+Enter</kbd> Same paragraph line break</p>
        </div>
      </div>
    </div>
  );
};

const RichTextEditor = ({ content, onChange, fontFamily, fontSize, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        underline: false,
      }),
      TextStyle.configure({ mergeNestedSpanStyles: true }),
      Color,
      FontFamily,
      FontSize,
      ParagraphStyle,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Start writing here...',
      }),
    ],
    content: toEditorHtml(content),
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      transformPastedHTML: cleanPastedHtml,
      // Pasting into an empty line keeps the first pasted paragraph's alignment/spacing instead of merging into the empty line
      transformPasted: (slice, view) => (
        slice.openStart > 0 && view.state.selection.$from.parent.content.size === 0
          ? new Slice(slice.content, 0, slice.openEnd)
          : slice
      ),
      clipboardTextParser: (text, $context, plain, view) => {
        const div = document.createElement('div');
        div.innerHTML = plainTextToParagraphs(text);
        return PMDOMParser.fromSchema(view.state.schema).parseSlice(div, { preserveWhitespace: true, context: $context });
      },
      attributes: {
        class: 'prose prose-lg max-w-none min-h-[500px] outline-none px-8 py-4 editor-content',
        style: `font-family: ${fontFamily || 'Lora'}; font-size: ${fontSize || '18px'}`,
        'data-testid': 'story-content-input',
      },
    },
  });

  // Sync external content changes (e.g. chapter switching) into the editor
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML();
      if (currentHtml !== content) {
        editor.commands.setContent(toEditorHtml(content));
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.style.fontFamily = fontFamily || 'Lora';
    editor.view.dom.style.fontSize = fontSize || '18px';
  }, [editor, fontFamily, fontSize]);

  return (
    <div data-testid="rich-text-editor">
      <Toolbar editor={editor} />
      {/* Writing area scrolls on its own so the toolbar never scrolls out of view */}
      <div
        className="overflow-y-auto overscroll-contain"
        style={{ height: 'max(320px, calc(100dvh - var(--editor-header-h, 0px) - 64px))' }}
        data-testid="editor-scroll-area"
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
